/**
 * v4 is the DEFAULT experience (Google/Apple/email sign-in + the new account
 * doors + the /claim guest→account page).
 *
 * This module used to carry a BUILD-TIME release mode (`qa | on | recovery | off`
 * via `REACT_APP_V4_MODE`) for the staged launch + rollback. Once the v4 cutover
 * merged to master, v4 is simply on everywhere, so the mode machinery is gone. The
 * two gates below remain as stable predicates — both now UNCONDITIONALLY TRUE — so
 * the ~26 consumer surfaces (55 call-sites) keep working without edits; the now-
 * vestigial `if (isV4Preview())` branches can be inlined in a later cleanup PR.
 *
 *   isV4Preview()       — the full v4 EXPERIENCE gate. Always true.
 *   isV4RecoveryDoors() — the OAuth/email sign-in doors gate (AuthGateContext reads
 *                         it to render V4SignInModal). Always true — v4 doors are
 *                         now the only doors.
 *
 * Boot hygiene is retained: `?v4` is stripped and any stale `thea:v4Preview`
 * localStorage is cleared (neither ever activates anything now), and
 * `window.__V4_MODE__` is stamped for runtime provenance (read by AuthGateContext's
 * error log + the post-deploy verification recipe).
 */

const STORAGE_KEY = 'thea:v4Preview'; // legacy flag key — cleared, never read for activation
const PARAM = 'v4';

/** The full v4 EXPERIENCE gate. v4 is the default → always true. */
export function isV4Preview(): boolean {
  return true;
}

/** The OAuth/email sign-in doors gate. v4 doors are the only doors → always true. */
export function isV4RecoveryDoors(): boolean {
  return true;
}

/**
 * Call ONCE at boot (index.js). Pure hygiene now: strip a stray `?v4` from the URL,
 * clear any stale `thea:v4Preview` localStorage (the old activation key — it can no
 * longer turn anything on, but clearing it removes a confusing artifact), and stamp
 * `window.__V4_MODE__` ('on') for runtime provenance. Safe with no window (SSR/tests).
 */
export function initV4PreviewFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    (window as unknown as { __V4_MODE__?: string }).__V4_MODE__ = 'on';
  } catch {
    /* ignore */
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / quota — nothing to clear */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has(PARAM)) {
      params.delete(PARAM);
      const qs = params.toString();
      const next = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      try {
        window.history.replaceState(window.history.state, '', next);
      } catch {
        /* replaceState can throw in odd embeds — harmless; the param never activated anything. */
      }
    }
  } catch {
    /* malformed URL — nothing to strip. */
  }
}
